/* eslint-disable no-restricted-globals */
import React, { useCallback, useEffect, useRef, useState } from "react";

import ShipeeziNewGrid, {
  Category,
  Column,
  GeneralActions,
} from "Components/AppGrid/shipeezi-new-grid";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { Color } from "../../../../components/Form/Button";
import { MoreOptions } from "../../../../components/MoreOptionsComponent/MoreOptions";
import TitlePage from "../../../../components/TitlePage";
import { MonthsOptions } from "../../../../constants";
import { InvoicesCharges } from "../../../../models/invoice.charges";
import { InvoicesShipment } from "../../../../models/invoices.shipment";
import { Filters, Searches } from "../../../../models/shipeezi.table";
import { BookingModes } from "../../../../util/enums";
// import SendCommunicationModal, {
//   SendCommunicationModalHandle,
// } from "../../../../components/Generic/SendCommunicationModal";
import { useAppState } from "Context/StateContext";
import { ResourceCanEnum, ResourceName } from "Util/enums/accessRule";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import DeleteInvoiceButton from "./components/DeleteButton";
import DeleteInvoiceModal, {
  DeleteInvoiceConfirmationModalHandles,
} from "./components/DeleteInvoiceModal";
import { InvoiceEmptySection } from "./components/EmptySection";
import UploadCsvInvoicesModal, { UploadCSVFileHandles } from "./components/UploadCsvInvoicesModal";
import useAction from "./hooks/useAction";
import useStyles, { StyledButton, UploadIcon } from "./styles";
import InvoiceViewComponent, { InvoiceViewHandles } from "./view";

interface InvoiceColumn extends Omit<Column, "name"> {
  name: keyof InvoicesCharges;
}

const Invoices = () => {
  const { currentUser, userPermissions } = useAppState();
  const { t: translate } = useTranslation();
  const { getAllInvoices, useDeleteInvoices } = useAction();
  const classes = useStyles();

  const uploadCSVRef = useRef<UploadCSVFileHandles>(null);
  const deleteModalRef = useRef<DeleteInvoiceConfirmationModalHandles>(null);
  const sideViewRef = useRef<InvoiceViewHandles>(null);
  // const communicationModalRef = useRef<SendCommunicationModalHandle>(null);

  const [invoices, setInvoices] = useState<InvoicesCharges[]>([]);
  const [totalItems, setTotalItems] = useState<number>(100);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>({});
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<number>();
  const [invoicePermission, setInvoicePermission] = useState<{
    view: boolean;
    edit: boolean;
    create: boolean;
    delete: boolean;
  }>({
    view: false,
    edit: false,
    create: false,
    delete: false,
  });

  const { data, isLoading, isRefetching, refetch } = getAllInvoices(
    page,
    pageSize,
    searches,
    filters
  );
  const { mutate: deleteInvoice, isLoading: loadingDelete } = useDeleteInvoices(
    selectedInvoice || 0,
    () => {
      deleteModalRef?.current?.close();
      refetch();
    }
  );

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setInvoicePermission({
        view: hasPermission(userPermissions, FN_NAMES.FUNCTION_INVOICES_GENERAL_VIEW),
        edit:
          checkResourceCan(userPermissions, ResourceName.INVOICES, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_INVOICES_GENERAL_EDIT),
        create:
          checkResourceCan(userPermissions, ResourceName.INVOICES, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_INVOICES_GENERAL_ADD_NEW),
        delete:
          checkResourceCan(userPermissions, ResourceName.INVOICES, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_INVOICES_GENERAL_DELETE),
      });
    }
  }, [userPermissions]);

  useEffect(() => {
    if (data && data.rows) {
      setInvoices(data.rows);
      setTotalItems(data.count);
    }
  }, [data]);

  const columns: InvoiceColumn[] = [
    { name: "id", title: `${translate("id")}` },
    {
      name: "invoiceShipment",
      title: `${translate("shipment")}`,
      formatText: (value?: InvoicesShipment) => value?.shipment,
      searchIdentifier: "invoiceShipment.shipment",
    },
    {
      name: "invoiceShipment",
      title: `${translate("mode")}`,
      formatComponent: (value?: InvoicesShipment) =>
        value?.mode ? translate(value.mode.toLowerCase()) : "",
      searchIdentifier: "invoiceShipment.mode",
    },
    {
      name: "invoiceShipment",
      title: translate("po"),
      formatComponent: (value?: InvoicesShipment) => {
        return (
          <MoreOptions
            options={value?.poNumber?.split(",") || []}
            title={translate("po")}
            manyOptionsToShow={1}
          />
        );
      },
      searchIdentifier: "invoiceShipment.poNumber",
    },
    {
      name: "invoiceShipment",
      title: `${translate("part")} #`,
      formatComponent: (value?: InvoicesShipment) => {
        return (
          <MoreOptions
            options={value?.partNumber?.split(",") || []}
            title={translate("part")}
            manyOptionsToShow={1}
          />
        );
      },
      searchIdentifier: "invoiceShipment.partNumber",
    },
    {
      name: "invoiceShipment",
      title: `${translate("supplierName")}`,
      formatComponent: (value?: InvoicesShipment) => value?.supplierName,
      searchIdentifier: "invoiceShipment.supplierName",
    },
    {
      name: "invoiceShipment",
      title: `${translate("originCountry")}`,
      formatComponent: (value?: InvoicesShipment) => value?.originCountry,
      searchIdentifier: "invoiceShipment.originCountry",
    },
    {
      name: "invoiceShipment",
      title: `${translate("origin")}`,
      formatComponent: (value?: InvoicesShipment) => value?.origin,
      searchIdentifier: "invoiceShipment.origin",
    },
    {
      name: "invoiceShipment",
      title: `${translate("destination")}`,
      formatComponent: (value?: InvoicesShipment) => value?.destination,
      searchIdentifier: "invoiceShipment.destination",
    },
    {
      name: "invoiceShipment",
      title: `${translate("eta")}`,
      formatComponent: (value?: InvoicesShipment) =>
        value?.eta ? format(new Date(value?.eta), "dd/MM/yyyy") : "",
      hideSearch: true,
    },
    {
      name: "invoiceShipment",
      title: `${translate("containerType")}`,
      formatComponent: (value?: InvoicesShipment) => value?.container,
      hideSearch: true,
    },
    {
      name: "invoiceShipment",
      title: `${translate("houseBill")}`,
      formatComponent: (value?: InvoicesShipment) => value?.houseBill,
      hideSearch: true,
    },
    {
      name: "invoiceShipment",
      title: `${translate("masterBill")}`,
      formatComponent: (value?: InvoicesShipment) => value?.masterBill,
      hideSearch: true,
    },
    {
      name: "invoiceShipment",
      title: `${translate("containers")}`,
      formatComponent: (value?: InvoicesShipment) => value?.containers,
      hideSearch: true,
    },
    {
      name: "invoiceShipment",
      title: `${translate("weight")} (KG)`,
      formatComponent: (value?: InvoicesShipment) => value?.weight,
      hideSearch: true,
    },
    {
      name: "invoiceShipment",
      title: `${translate("teu")}`,
      formatComponent: (value?: InvoicesShipment) => value?.teu,
      hideSearch: true,
    },
    {
      name: "invoiceShipment",
      title: `${translate("cbm")}`,
      formatComponent: (value?: InvoicesShipment) => value?.cbm,
      hideSearch: true,
    },
    {
      name: "invoiceShipment",
      title: `${translate("cartons")}`,
      formatComponent: (value?: InvoicesShipment) => value?.cartons,
      hideSearch: true,
    },
    {
      name: "invoiceShipment",
      title: `${translate("hblCountMode")}`,
      formatComponent: (value?: InvoicesShipment) => value?.hblMode,
      hideSearch: true,
    },
    {
      name: "chargeCode",
      title: `${translate("chargeCode")}`,
      hideSearch: true,
    },
    {
      name: "description",
      title: `${translate("description")}`,
      hideSearch: true,
    },
    {
      name: "overseasAmount",
      title: `${translate("overseasAmount")}`,
      hideSearch: true,
    },
    {
      name: "exchangeRateCurrency",
      title: `${translate("exchangeRateCurrency")}`,
      hideSearch: true,
    },
    {
      name: "exchangeRate",
      title: `${translate("exchangeRate")}`,
      hideSearch: true,
    },
    {
      name: "invoicedLocalAmount",
      title: `${translate("invoicedLocalAmount")}`,
      hideSearch: true,
    },
    {
      name: "gstAmount",
      title: `${translate("gstAmount")}`,
      hideSearch: true,
    },
    {
      name: "invoiceNumber",
      title: `${translate("invoiceNumber")}`,
      hideSearch: true,
    },
    {
      name: "invoiceDate",
      title: `${translate("invoiceDate")}`,
      formatComponent: (value?: Date) => (value ? format(new Date(value), "dd/MM/yyyy") : ""),
      hideSearch: true,
    },
    {
      name: "invoiceDueDate",
      title: `${translate("invoiceDueDate")}`,
      formatComponent: (value?: Date) => (value ? format(new Date(value), "dd/MM/yyyy") : ""),
      hideSearch: true,
    },
    {
      name: "invoiceShipment",
      title: `${translate("delivered")}`,
      formatComponent: (value?: InvoicesShipment) =>
        value?.delivered ? format(new Date(value?.delivered), "dd/MM/yyyy") : "",
      hideSearch: true,
    },
    {
      name: "autoRate",
      title: `${translate("autoRate")}`,
      hideSearch: true,
    },
  ];

  const handleChangeCurrentPage = (currentPage: any) => {
    setPage(currentPage - 1);
  };

  const handleChangeRowsPerPage = async (newPageSize: any) => {
    setPageSize(newPageSize);
  };

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const filterCategories: Category[] = [
    {
      name: translate("etaMonth"),
      column: "etaMonth",
      options: MonthsOptions.map((option) => {
        return {
          ...option,
          label: translate(option.label),
        };
      }),
    },
    {
      name: translate("mode"),
      column: "invoiceShipment.mode",
      options: [
        { value: BookingModes.SEA, label: translate("sea") },
        { value: BookingModes.AIR, label: translate("air") },
        { value: BookingModes.ROAD, label: translate("road") },
      ],
    },
  ];

  const openUploadCsv = () => {
    uploadCSVRef?.current?.open(uuid());
  };
  const onOpenDeleteModal = (id: number) => {
    const invoice = invoices.find((value) => value.id === id);

    setSelectedInvoice(id);

    deleteModalRef?.current?.open(id, invoice?.id?.toString());
  };
  const onDeleteInvoice = (id: number) => {
    deleteInvoice(id);
  };

  const renderDeleteButton = (id: number) => {
    if (!isShowAction(currentUser, invoicePermission.delete)) return null;

    return (
      <DeleteInvoiceButton
        id={id}
        handleOnClick={onOpenDeleteModal}
        loading={loadingDelete && id === selectedInvoice}
      />
    );
  };

  // const handleCommunicationButton = useCallback(
  //   (id: string | number, row: InvoicesCharges) => {
  //     return (
  //       <Tooltip title={translate("communication")} placement="top">
  //         <div
  //           onClick={() => {
  //             communicationModalRef.current?.onOpen({
  //               entityType: 'invoice',
  //               entityId: Number(row.id),
  //               entity: row,
  //               customer: undefined,
  //             });
  //           }}
  //           className={classes.button}
  //           data-cy="communication-button"
  //         >
  //           <HiOutlineMail size={18} color="#475467" />
  //         </div>
  //       </Tooltip>
  //     );
  //   },
  //   [translate, classes.button]
  // );
  const viewInvoice = (invoiceId: number | string) => {
    if (invoiceId && !Number.isNaN(Number(invoiceId))) sideViewRef.current?.open(Number(invoiceId));
  };
  const renderUploadCsv = useCallback(() => {
    if (!isShowAction(currentUser, invoicePermission.create)) return null;

    return (
      <StyledButton customColor={Color.SECONDARY} onClick={openUploadCsv}>
        <UploadIcon color="#ffffff" size={16} />
        {translate("uploadCsv")}
      </StyledButton>
    );
  }, []);

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["onUploadCsv"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: filterCategories,
      onChange: handleFilter,
    },
    onUploadCsv: renderUploadCsv(),
  };

  const renderScreen = () => {
    if (!isShowAction(currentUser, invoicePermission.view)) return null;
    if (
      !isLoading &&
      !searches.length &&
      !Object.keys(filters).length &&
      (!data || !data?.rows?.length)
    ) {
      return (
        <div className={classes.dispatchesGridWrapper}>
          <InvoiceEmptySection onAdd={openUploadCsv} />
        </div>
      );
    }

    return (
      <div className={classes.dispatchesGridWrapper} data-cy="invoicesListGrid">
        <ShipeeziNewGrid
          data={invoices}
          totalItems={totalItems}
          loadingData={isLoading || isRefetching}
          namePage={translate("invoices")}
          emptyData={isLoading ? translate("loadingInvoices") : translate("noInformationToBeShown")}
          columns={columns}
          currentPage={page}
          pageSize={pageSize}
          pageSizeList={totalItems > pageSize ? [20, 40, 60, 100] : []}
          onChangePageSize={handleChangeRowsPerPage}
          onNextPage={handleChangeCurrentPage}
          onPreviousPage={handleChangeCurrentPage}
          onGoToPage={handleChangeCurrentPage}
          generalActions={generalActions}
          rowActions={{
            handleDetails: (invoiceId) => viewInvoice(invoiceId),
          }}
          rowActionsExtra={[
            // {
            //   title: translate("communication"),
            //   key: "communication",
            //   buttonAndAction: handleCommunicationButton,
            // },
            {
              title: translate("upload"), // nao sei o q faz ainda - nao faz sentido
              key: "upload",
              buttonAndAction: () => {},
            },
            {
              title: translate("delete"),
              key: "delete",
              buttonAndAction: renderDeleteButton,
            },
          ]}
          positionActionsColumn="right"
          selecteds={selectedItems}
          handleSelect={setSelectedItems}
          showSelection={false}
          showFooterGrid={totalItems !== 0}
        />
      </div>
    );
  };

  return (
    <>
      <div data-cy="invoices-grid">
        <TitlePage title={translate("invoices")} />
        {renderScreen()}
        <UploadCsvInvoicesModal ref={uploadCSVRef} refetchInvoices={refetch} />
        <DeleteInvoiceModal
          ref={deleteModalRef}
          onSubmit={onDeleteInvoice}
          loadingAction={loadingDelete}
        />
        <InvoiceViewComponent ref={sideViewRef} />
        {/*<SendCommunicationModal
          ref={communicationModalRef}
          onRefetch={refetch}
        /> */}
      </div>
    </>
  );
};

export default Invoices;
